import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Hammer, Loader2, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-10 h-10 rounded-xl bg-[#FF5A00] flex items-center justify-center"><Hammer className="w-5 h-5 text-white" /></div>
          <span className="font-display text-xl font-bold tracking-tight">SelLend</span>
        </div>

        {!token ? (
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Invalid reset link</h1>
            <p className="text-muted-foreground mt-2">This link is missing or malformed.</p>
            <Link to="/login" className="inline-block mt-5 bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors">Back to sign in</Link>
          </div>
        ) : done ? (
          <div>
            <CheckCircle2 className="w-12 h-12 text-[#2E7D32]" />
            <h1 data-testid="reset-success" className="font-display text-2xl font-bold tracking-tight mt-4">Password updated</h1>
            <p className="text-muted-foreground mt-2">You can now sign in with your new password.</p>
            <button onClick={() => navigate("/login")} className="mt-5 bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors">Go to sign in</button>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold tracking-tight">Set a new password</h1>
            <p className="text-muted-foreground mt-2 mb-8">Choose a strong password you'll remember.</p>
            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className="pl-9 h-12 bg-white border-black/10 rounded-xl" />
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className="pl-9 h-12 bg-white border-black/10 rounded-xl" />
              </div>
              <button data-testid="reset-submit" disabled={busy} className="w-full h-12 flex items-center justify-center gap-2 bg-[#0A0A0A] text-white rounded-full font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
